<template>
  <div v-if="organization" class="mb-12">
    <h1 class="mt-2 mb-4 font-sans text-n-gray-dark">Additional Information</h1>

    <div class="mb-12">
      <label>Do you want to be represented as yourself or your organization?</label>
      <p>
        {{ organization.type === 'individual' ? 'Myself' : 'My organization' }}
      </p>
    </div>

    <div class="w-full mb-12">
      <label>
        {{ isIndividual ? 'Public' : 'Organization' }} name <span class="text-red">*</span>
      </label>
      <input v-validate="'required|min:5'" name="name" type="text" class="w-full" placeholder="Public name displayed" v-model="form.name" />
      <span v-show="errors.has('name')" class="error text-error-red text-lg">
        {{ errors.first('name') }}
      </span>
    </div>

    <template v-if="!isIndividual">
      <div class="mb-12">
        <label>
          Organization type
          <span class="text-red">*</span>
        </label>
        <select v-model="form.subtype" v-validate="'required'" name="organization_subtype">
          <option :value="null" disabled> -- please select -- </option>
          <option v-for="subtype in options.organizationSubtypes" :key="subtype" :value="subtype">
            {{ subtype }}
          </option>
        </select>
        <span v-show="errors.has('organization_subtype')" class="error text-error-red text-lg">
          {{ (errors.first('organization_subtype') || '').replace('_', ' ') }}
        </span>
      </div>
    </template>

    <div class="w-full mb-12">
      <label>Website</label>
      <input name="website" v-validate="{ url: { require_protocol: true } }" type="text" class="w-full" placeholder="https://letsreimagine.org" v-model="form.website"/>
      <span v-show="errors.has('website')" class="error text-error-red text-lg">
        {{ errors.first('website') }}
      </span>
    </div>

    <div class="mb-12">
      <label>
        What country {{ isIndividual ? 'are you' : 'is your orgainzation' }} located in?
        <span class="text-red">*</span>
      </label>
      <select v-model="form.country" v-validate="'required'" name="country">
        <option :value="null" disabled> -- please select -- </option>
        <option v-for="country in options.countries" :key="country.code" :value="country.code">
          {{ country.name }}
        </option>
      </select>
      <span v-show="errors.has('country')" class="error text-error-red text-lg">
        {{ errors.first('country') }}
      </span>
    </div>

    <div class="flex flex-col md:flex-row mb-12">
      <div :class="[isFromUnitedStates ? 'w-full md:w-1/2' : 'w-full']">
        <label>
          City <span class="text-red">*</span>
        </label>
        <input v-validate="'required'" name="city" v-model="form.city" type="text" placeholder="City" />
        <span v-show="errors.has('city')" class="error text-error-red text-lg">
          {{ errors.first('city') }}
        </span>
      </div>
      <template v-if="isFromUnitedStates">
        <div class="w-full md:w-1/4 pl-0 md:pl-2 mt-4 md:mt-0">
          <label>
            State <span class="text-red">*</span>
          </label>
          <select v-validate="'required'" name="state" v-model="form.state">
            <option :value="null">-- select --</option>
            <option v-for="state in options.states" :key="state.code">
              {{ state.name }}
            </option>
          </select>
          <span v-show="errors.has('state')" class="error text-error-red text-lg">
            {{ errors.first('state') }}
          </span>
        </div>

        <div class="w-full md:w-1/4 pl-0 md:pl-2 mt-4 md:mt-0">
          <label>
            Zip code <span class="text-red">*</span>
          </label>
          <input v-validate="'required'" name="zip_code" v-model="form.zip_code" type="text" placeholder="Zip Code" />
          <span v-show="errors.has('zip_code')" class="error text-error-red text-lg">
            {{ (errors.first('zip_code') || '').replace('_', ' ') }}
          </span>
        </div>
      </template>
    </div>

    <button class="button" @click="saveAdditionalInfo">
      Update
    </button>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { optionsAPI } from "../../../api/optionsAPI";
import {geocodeAPI} from "../../../api/geocodeAPI";
import {organizationAPI} from "../../../api/organizationAPI";

export default {
  name: "OrganizationAdditionalInfo",
  computed: {
    ...mapGetters({
      organization: "organization",
      user: "user"
    }),
    isIndividual() {
      return this.organization.type === 'individual';
    },
    isFromUnitedStates() {
      return this.form.country === 'USA';
    },
  },
  data() {
    return {
      options: {
        countries: geocodeAPI.getCountries(),
        organizationSubtypes: optionsAPI.getOrganizationSubtypes(),
        states: geocodeAPI.getStates(),
      },
      form: {
        type: '',
        subtype: null,
        name: '',
        website: '',
        country: null,
        state: null,
        city: '',
        zip_code: '',
      }
    }
  },
  watch: {
    organization() {
      this.mapForm();
    },
    user() {
      this.mapForm();
    }
  },
  methods: {
    getOrganization() {
      let id = this.user.organization_id;
      this.$store.dispatch("getOrganization", { id });
    },
    mapForm() {
      this.form.type = this.organization.type;
      this.form.subtype = this.organization.subtype;
      this.form.name = this.organization.name;
      this.form.website = this.organization.url;
      this.form.country = this.organization.country_code || null;
      this.form.city = this.organization.city;
      this.form.state = this.organization.state || null;
      this.form.zip_code = this.organization.zip_code;
    },
    async saveAdditionalInfo() {
      const hasNoFormErrors = await this.$validator.validateAll();

      if (!hasNoFormErrors) {
        // this.gotoFirstValidationError();
        this.getOrganization();
        return;
      }

      const response = await organizationAPI.updateUsers(this.form);

      if (response.success) {
        this.$toasted.show("Additional information has been updated");
        return;
      }

      Object.keys(response.errors).forEach(formAttr => {
        const errorMessage = response.errors[formAttr].find(() => true);
        this.errors.add({ field: formAttr, msg: errorMessage });
      });
    }
  },
  created() {
    this.getOrganization();
  }
}
</script>

<style scoped>

</style>